import React, {useEffect, useState} from 'react'
import {Controller, useForm} from 'react-hook-form'
import {useDispatch, useSelector} from 'react-redux'
import {useNavigate, useParams} from 'react-router-dom'
import {joiResolver} from '@hookform/resolvers/joi'
import {AiOutlineInfoCircle} from 'react-icons/ai'
import Editor from 'react-simple-code-editor'
import _ from 'lodash'
import {highlight, languages} from 'prismjs'
import {
  VmoButton,
  VmoDropdown,
  VmoForm,
  VmoFormField,
  VmoFormInput,
  VmoFormTextArea,
  VmoList,
  VmoListContent,
  VmoListHeader,
  VmoListItem,
  VmoRadio,
  VmoTab,
  VmoTabPane,
  VmoTooltip,
} from 'vmo-library'

import {CREATE_FEEDBACK_FORM, EDIT_FEEDBACK_FORM, GET_ALL_MAIL_INBOX, SINGLE_FEEDBACK_FORM} from 'actions/types'
import {getAllMailInbox} from 'actions/inbox'
import {createFeedbackForm, editFeedbackForm, getFeedbackForm, getSingleFeedbackForm} from 'actions/settings_channels'
import Page from 'components/common/Page'
import LockPermissionTooltip from 'components/common/LockPermissionTooltip'
import {copyToClipboardWithoutInput, get, getPathWithPrefix} from 'utils/helper'
import {feedbackSchema} from 'validation/settings/channels/feedback.schema'
import useUserPermissions from 'hooks/pure/useUserPermission'
import useLearnMoreUrl from 'hooks/pure/useLearnMoreUrl'
import {INBOX_TYPES} from 'constants/enums'
import {SETTINGS_PATH} from 'constants/path'
import useApiResponse from 'hooks/impure/useApiResponse'
import {Input, TextArea} from 'components/common/Form'
import {useUnsavedChanges} from 'hooks/pure/useUnsavedChanges'

const formFieldArray = [
  {
    title: 'Your Name',
    value: 'name',
    isDisable: true,
    checked: true,
  },
  {
    title: 'Your Email',
    value: 'email',
    isDisable: true,
    checked: true,
  },
  {
    title: 'Your Number',
    value: 'phone',
  },
  {
    title: 'Subject',
    value: 'subject',
    isDisable: true,
    checked: true,
  },
  {
    title: 'Message',
    value: 'message',
    isDisable: true,
    checked: true,
  },
  {
    outSider: true,
    title: 'Allow customers to attach files',
    value: 'attachments',
  },
  {
    outSider: true,
    title: 'Enable captcha to prevent spam',
    value: 'captcha',
  },
]

const SetupForm = props => {
  const {isActive, toggleModal, id} = props
  const dispatch = useDispatch()
  const {
    handleSubmit,
    formState: {errors, isDirty},
    control,
    reset,
  } = useForm({
    mode: 'onTouched',
    shouldFocusError: true,
    reValidateMode: 'onChange',
    submitFocusError: true,
    shouldUnregister: false,
    resolver: joiResolver(feedbackSchema),
    defaultValues: {
      headerText: '',
      subHeaderText: '',
      submitText: '',
      submitMsg: '',
      allowFields: {
        email: true,
        name: true,
        message: true,
        subject: true,
        phone: false,
      },
      captcha: false,
      attachments: false,
      mailboxId: -1,
    },
  })

  const {feedbackForm} = useSelector(state => state.settingsChannels)
  const {isLoadingData, successLabels = [], type = []} = useSelector(state => state.apiReducer)

  const {setWarningModal} = useUnsavedChanges()
  const {channelsPermissions} = useUserPermissions()
  const allowManageContactFormRole = channelsPermissions?.manage_contact_form

  const resetData = () => {
    if (feedbackForm) {
      const fields = feedbackForm?.params?.allowFields.reduce((acc, curr) => {
        return {...acc, [curr]: true}
      }, {})
      const resetValues = {
        ...feedbackForm?.params,
        allowFields: fields,
        mailboxId: feedbackForm?.mailboxId,
        submitMsg: feedbackForm?.submitMsg,
        submitText: feedbackForm?.submitText,
        headerText: feedbackForm?.headerText,
        subHeaderText: feedbackForm?.subHeaderText,
      }
      reset(resetValues)
      setWarningModal(false)
    }
  }

  useEffect(() => {
    if (id && isDirty) {
      setWarningModal(isDirty)
    }
    return () => {
      setWarningModal(false)
    }
  }, [isDirty])

  useEffect(() => {
    if (successLabels.includes(SINGLE_FEEDBACK_FORM)) {
      resetData()
    }
    if (successLabels.includes(CREATE_FEEDBACK_FORM)) {
      dispatch(getFeedbackForm())
      toggleModal(false)
    }
  }, [successLabels])

  useEffect(() => {
    if (id) {
      resetData()
    }
  }, [id])

  const submitForm = data => {
    const active = _.keys(_.pickBy(data.allowFields))
    const newContactForm = {...data, allowFields: active, isActive}
    if (props.id) {
      dispatch(editFeedbackForm(feedbackForm.id, newContactForm))
    } else {
      dispatch(createFeedbackForm(newContactForm))
    }
  }

  return (
    <VmoForm className="inlineForm mt-4 w-1/2 errorLabel">
      <Input
        name="headerText"
        control={control}
        label="Heading"
        tooltipProps={{content: 'Specify heading for contact form'}}
        maxLength={30}
        required
        width={8}
      />

      <Input
        name="subHeaderText"
        control={control}
        label="Subheading"
        tooltipProps={{content: 'Specify sub-heading for contact form'}}
        maxLength={100}
        required
        width={8}
      />
      <VmoFormField width={8}>
        <div className="info-header">
          <label>Select Receiving Inbox</label>
          <VmoTooltip
            content="Inbox in which you want to receive queries via contact form"
            trigger={<AiOutlineInfoCircle />}
          />
        </div>
        <Controller
          control={control}
          name="mailboxId"
          render={({field: {value, onChange, onBlur}}) => (
            <VmoDropdown
              selection
              value={value}
              options={props.listMailBox}
              onChange={(e, {value}) => {
                onChange(value)
              }}
              onBlur={onBlur}
            />
          )}
        />
      </VmoFormField>
      <VmoFormField>
        <label>Form Fields</label>
        <VmoList relaxed className="swtichList">
          {formFieldArray.map(item => {
            return (
              <VmoListItem key={item.value}>
                <VmoListContent>
                  <VmoListHeader>
                    {item.title}
                    <Controller
                      defaultValue={false}
                      control={control}
                      name={item.outSider ? `${item.value}` : `allowFields.${item.value}`}
                      render={({field: {value, onChange}}) => (
                        <VmoRadio
                          toggle
                          checked={value}
                          value={value}
                          disabled={item.isDisable}
                          onChange={(e, {checked}) => {
                            onChange(checked)
                          }}
                        />
                      )}
                    />
                  </VmoListHeader>
                </VmoListContent>
              </VmoListItem>
            )
          })}
        </VmoList>
      </VmoFormField>
      <Input
        name="submitText"
        control={control}
        label="Submit Button Text"
        maxLength={100}
        required
        placeholder="Submit"
        width={8}
      />
      <TextArea
        name="submitMsg"
        control={control}
        label="Submit Message"
        tooltipProps={{
          content: 'This message will be displayed after the user submits the form data',
        }}
        required
        maxLength={100}
        placeholder="Thank you for your feedback"
        width={8}
      />
      <div className="setActionBtn">
        <VmoButton basic className="mr-3" onClick={() => (id ? resetData : toggleModal(false))}>
          Cancel
        </VmoButton>
        <LockPermissionTooltip isRoleAccessDenied={!allowManageContactFormRole}>
          <VmoButton
            primary
            className={!allowManageContactFormRole ? 'disabled-button' : ''}
            loading={isLoadingData && type.includes(EDIT_FEEDBACK_FORM)}
            disabled={!isDirty || (isLoadingData && type.includes(EDIT_FEEDBACK_FORM))}
            {...(allowManageContactFormRole && {
              onClick: handleSubmit(data => submitForm(data)),
            })}
          >
            {id ? 'Update' : 'Create'}
          </VmoButton>
        </LockPermissionTooltip>
      </div>
    </VmoForm>
  )
}

const Feedback = props => {
  const {toggleModal} = props
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const {id} = useParams()

  const [code, setCode] = useState('')
  const [copySuccess, setCopySuccess] = useState('COPY')
  const [isActive, setIsActive] = useState(true)
  const [listMailBox, setListMailBox] = useState([])

  const {feedbackForm} = useSelector(state => state.settingsChannels)
  const {successLabels = []} = useSelector(state => state.apiReducer)

  const {setWarningModal} = useUnsavedChanges()
  const {settingsLearnMoreUrl} = useLearnMoreUrl()
  const addContactFormLearnMore = settingsLearnMoreUrl?.channels?.contactForm?.addLearnMore
  const updateContactFormLearnMore = settingsLearnMoreUrl?.channels?.contactForm?.updateLearnMore
  const embedCodeLearnMore = settingsLearnMoreUrl?.channels?.contactForm?.embedCodeLearnMore

  useApiResponse({
    action: getAllMailInbox,
    enabled: true,
    label: GET_ALL_MAIL_INBOX,
    storePath: 'mailInbox.inbox',
    onSuccess: mailboxList => {
      const list = mailboxList
        ?.filter(inbox => (inbox.type === INBOX_TYPES.EMAIL ? inbox.emailVerified : true))
        ?.map(inbox => ({
          value: inbox.id,
          key: inbox.id,
          text: inbox.name,
        }))

      setListMailBox(list || [])
    },
  })

  useEffect(() => {
    if (id) {
      dispatch(getSingleFeedbackForm(id))
    } else {
      dispatch(getFeedbackForm())
    }
  }, [dispatch, id])

  useEffect(() => {
    if (successLabels.includes(SINGLE_FEEDBACK_FORM)) {
      setCode(get(['code'], feedbackForm, ''))
      setIsActive(get(['isActive'], feedbackForm, false))
    }
    if (successLabels.includes(EDIT_FEEDBACK_FORM)) {
      setWarningModal(false)
      dispatch({type: SINGLE_FEEDBACK_FORM, payload: {}})
      navigate(-1)
    }
  }, [successLabels, listMailBox])

  const panes = [
    {
      menuItem: {
        key: 'setup',
        icon: 'clipboard list',
        content: 'Setup',
      },
      render: () => (
        <VmoTabPane attached={false}>
          <SetupForm {...props} id={id} listMailBox={listMailBox} isActive={isActive} />
        </VmoTabPane>
      ),
    },
    {
      menuItem: {
        key: 'embed-code',
        icon: 'code',
        disabled: !id,
        content: 'Embed Code',
      },
      render: () => (
        <VmoTabPane attached={false}>
          <h3 className="mt-4">Embed code</h3>
          <p className="lead mb-4">
            Please copy and paste the code below anywhere before the body tag in the html template of your site or every
            page where you'd like to provide the feedback form , For more detailed explanation of setting this up{' '}
            <a href={embedCodeLearnMore}>Learn More</a>
          </p>
          <div className="codeTextView">
            <Editor
              disabled
              value={decodeURIComponent(code)}
              highlight={code => highlight(code, languages.html)}
              padding={10}
              textareaId="codeArea"
              style={{
                border: '1px solid var(--border-color)',
                fontFamily: 'monospace',
                fontSize: 16,
                outline: 0,
                background: 'var(--bg-color)',
                borderRadius: 10,
              }}
            />
            <div className="setActionBtn">
              <VmoButton
                size="tiny"
                primary
                onClick={e => copyToClipboardWithoutInput(decodeURIComponent(code), setCopySuccess)}
              >
                {copySuccess}
              </VmoButton>
            </div>
          </div>
        </VmoTabPane>
      ),
    },
  ]

  const handleActive = () => {
    setIsActive(!isActive)
    // dispatch(editFormStatus({isActive: !isActive}))
  }

  // Main Component
  return (
    <Page
      header="Contact Form"
      headerbackbtn={{
        onClick: () => {
          if (id) {
            setWarningModal(false)
            navigate(getPathWithPrefix(SETTINGS_PATH.PREFIX, SETTINGS_PATH.CONTACT_FORM))
          } else {
            toggleModal(false)
          }
        },
      }}
      learnMoreUrl={id ? updateContactFormLearnMore : addContactFormLearnMore}
      subHeader="The feedback form lets you embed a ticket form directly ."
      headerRadioButton={{
        checked: isActive,
        onChange: handleActive,
        name: 'feedbackform',
      }}
      table={false}
      headerDivider
    >
      <VmoTab menu={{secondary: true, pointing: true, color: 'blue'}} panes={panes} />
    </Page>
  )
}
export default Feedback
